import { useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ThemeProvider, useTheme } from '@mui/material/styles';
import {
  AppShell as WorkspaceAppShell,
  WorkspaceThemeProvider,
  useShellPrefs,
  type WsShellApp,
  type WsShellAppGroup,
  type WsShellModule,
  type WsShellSection,
} from '@platform/ui-kit/workspace';
import {
  MODULE_GROUP_LABELS,
  MODULE_GROUP_ORDER,
  type resolveNavigation,
} from '@platform/entitlements';
import type { ModuleSection, PortalModule } from '../api';
import { AttributionRow } from './AttributionRow';
import { moduleGlyph } from './moduleIcons';
import { keepWorkspaceUi } from './keepWorkspaceUi';

export interface PortalShellProps {
  brand: string;
  activeSectionKey?: string;
  activeTrailKeys?: readonly string[];
  breadcrumbs: { label: string; path?: string }[];
  onNavigate: (path: string) => void;
  user?: { displayName: string; email?: string };
  onSignOut?: () => void;
  versionLabel?: string | undefined;
  /** The modules this person can open (entitled and assigned). */
  modules: readonly PortalModule[];
  navLocation: ReturnType<typeof resolveNavigation>;
  /** Keys their favourites and recents; without one nothing is remembered. */
  userId?: string;
  children: ReactNode;
}

/**
 * The portal's one shell: the Decision Workspace frame with the navigation drawer, fed from the
 * module catalogue and the person's entitlements. The Classic rail is gone (owner ruling
 * 2026-10-23); every ported screen is unchanged and renders inside it.
 */
export function PortalShell(props: PortalShellProps) {
  return <WorkspaceShell {...props} />;
}

const toSection = (s: ModuleSection): WsShellSection => ({
  key: s.key,
  label: s.label,
  path: s.path,
  ...(s.startsBottomGroup ? { startsBottomGroup: true } : {}),
  ...(s.sections ? { sections: s.sections.map(toSection) } : {}),
});

function WorkspaceShell({
  modules,
  navLocation,
  userId,
  brand,
  activeSectionKey,
  activeTrailKeys,
  breadcrumbs,
  onNavigate,
  user,
  onSignOut,
  versionLabel,
  children,
}: PortalShellProps) {
  // The theme the page had before the workspace one is entered: the originals shown inside this
  // shell must keep it (CLAUDE.md 1.2), so it is put back around the screen area.
  const classicTheme = useTheme();
  const { pathname, search } = useLocation();
  const prefs = useShellPrefs(userId);

  // The same filter the Classic drawer applies: a module with no sections has no screens yet and
  // stays on the launcher only.
  const appGroups = useMemo<WsShellAppGroup[]>(
    () =>
      MODULE_GROUP_ORDER.map((group) => ({
        label: MODULE_GROUP_LABELS[group],
        apps: modules
          .filter((m) => m.group === group && m.sections.length > 0)
          .map<WsShellApp>((m) => ({
            id: m.id,
            label: m.navLabel,
            icon: moduleGlyph(m.icon),
            path: m.path,
            homePath: `${m.path}?start=1`,
            sections: m.sections.map(toSection),
          })),
      })).filter((g) => g.apps.length > 0),
    [modules],
  );

  const activeModule = useMemo<WsShellModule | undefined>(() => {
    const m = navLocation.module;
    if (!m) return undefined;
    return {
      id: m.id,
      label: m.navLabel,
      icon: moduleGlyph(m.icon),
      // The module header is an explicit request for the product's own home, as in the Classic
      // drawer, so a skipped start screen does not swallow the click.
      path: `${m.path}?start=1`,
      sections: m.sections.map(toSection),
    };
  }, [navLocation.module]);

  // Remember where the person has been, once per screen.
  const { recordVisit } = prefs;
  const moduleLabel = navLocation.module?.navLabel;
  const here = breadcrumbs[breadcrumbs.length - 1]?.label;
  useEffect(() => {
    if (!moduleLabel || !here) return;
    recordVisit({
      label: here,
      ...(here !== moduleLabel ? { context: moduleLabel } : {}),
      path: pathname,
    });
  }, [pathname, moduleLabel, here, recordVisit]);

  return (
    <WorkspaceThemeProvider baseline={false} focusRing={false}>
      <WorkspaceAppShell
        brand={brand}
        appGroups={appGroups}
        {...(activeModule ? { activeModule } : {})}
        {...(activeSectionKey ? { activeSectionKey } : {})}
        {...(activeTrailKeys ? { activeTrailKeys } : {})}
        breadcrumbs={breadcrumbs}
        onNavigate={(path) => onNavigate(keepWorkspaceUi(path, search))}
        {...(user ? { user } : {})}
        {...(onSignOut ? { onSignOut } : {})}
        favoriteIds={prefs.favoriteIds}
        onToggleFavorite={prefs.toggleFavorite}
        recents={prefs.recents}
        footer={<AttributionRow versionLabel={versionLabel} />}
        contentScroll
      >
        <ThemeProvider theme={classicTheme}>{children}</ThemeProvider>
      </WorkspaceAppShell>
    </WorkspaceThemeProvider>
  );
}
